import { InProgressScan } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import log from 'loglevel'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { SCAN_RECEPTOR_INTERVAL } from 'src/pages/Receptors/receptors.constants'
import { useInProgressReceptorScans } from 'src/pages/Receptors/receptors.hooks'

type ReceptorScanContextType = {
  receptorScanRunning: boolean
  setReceptorScanRunning: React.Dispatch<React.SetStateAction<boolean>>
  scanInProgressState: Record<string, boolean>
  setScanInProgressState: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >
}

export const ReceptorScanContext = createContext<ReceptorScanContextType>({
  receptorScanRunning: false,
  setReceptorScanRunning: () => null,
  scanInProgressState: {},
  setScanInProgressState: () => null,
})

export default ReceptorScanContext

export const ReceptorScanContextProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  const { data, isLoading, mutate, error } = useInProgressReceptorScans()
  const interval = useRef<number | null>(0)
  const [receptorScanRunning, setReceptorScanRunning] = useState(false)
  const [scanInProgressState, setScanInProgressState] = useState<
    Record<string, boolean>
  >({})
  const currentScansMap = useRef<Record<string, boolean>>({})

  const getScansMap = useCallback(
    (scans: InProgressScan[]): Record<string, boolean> => {
      return scans.reduce((acc, scan) => {
        const sortedImportKeys = scan
          .getImportKeysList()
          ?.sort((a: string, b: string) => {
            return a.localeCompare(b)
          })
        if (sortedImportKeys.length > 0) {
          const importKeys = sortedImportKeys?.join(',')
          acc[`${scan.getReceptorId()}-${importKeys}`] = true
        } else {
          acc[scan.getReceptorId()] = true
        }
        return acc
      }, {} as Record<string, boolean>)
    },
    [],
  )

  const getScanInProgressState = useCallback(
    (inProgressScans: InProgressScan[]): Record<string, boolean> => {
      const newScansMap = getScansMap(inProgressScans)
      const previousScansMap = currentScansMap.current
      // compare the ids in the current state to the previous state
      // if the current state does not contain an id from the previous state,
      // that scan has finished, so we show a toast
      // Right now, these toasts will only appear on the receptors index or show pages
      // In the future, we may want to move the context provider, so they appear everywhere in the app
      Object.keys(previousScansMap).forEach((scanId) => {
        if (!newScansMap[scanId]) {
          showInfoToast(`Evidence collection complete.`)
        }
      })

      return newScansMap
    },
    [currentScansMap, getScansMap],
  )

  const stopPolling = useCallback(() => {
    if (!interval.current) return
    clearInterval(interval.current)
    interval.current = null
  }, [interval])

  const startPolling = useCallback(() => {
    if (interval.current) return
    interval.current = window.setInterval(
      async () => await mutate(),
      SCAN_RECEPTOR_INTERVAL,
    )
  }, [interval, mutate])

  useEffect(() => {
    if (!data || isLoading) return
    else if (error) {
      log.error('Error fetching in-progess receptor scans:', error)
      return
    }
    const scans = data.getScansList()
    const scansInProgress = getScanInProgressState(scans)
    setScanInProgressState(scansInProgress)
    if (Object.values(scansInProgress).length > 0) {
      setReceptorScanRunning(true)
    } else {
      setReceptorScanRunning(false)
    }
    currentScansMap.current = scansInProgress

    startPolling()

    return () => stopPolling()
  }, [
    data,
    isLoading,
    error,
    startPolling,
    stopPolling,
    getScanInProgressState,
  ])

  const value = useMemo(
    () => ({
      receptorScanRunning,
      setReceptorScanRunning,
      scanInProgressState,
      setScanInProgressState,
    }),
    [receptorScanRunning, scanInProgressState],
  )

  return (
    <ReceptorScanContext.Provider value={value}>
      {children}
    </ReceptorScanContext.Provider>
  )
}

export const useReceptorScanContext = (): ReceptorScanContextType => {
  return useContext(ReceptorScanContext)
}
